if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.purchaseOptions = this.querySelector('[data-purchase-options]');
        this.initPurchaseOptions();
      }

      initPurchaseOptions() {
        if (!this.purchaseOptions) return;

        this.sellingPlanSelect = this.purchaseOptions.querySelector('[data-selling-plan-select]');
        this.purchaseOptionInputs = this.purchaseOptions.querySelectorAll('[data-purchase-option]');
        if (!this.sellingPlanSelect || !this.purchaseOptionInputs.length) return;

        this.purchaseOptionInputs.forEach((input) => {
          input.addEventListener('change', () => this.updatePurchaseOptions());
        });

        this.updatePurchaseOptions();
      }

      updatePurchaseOptions() {
        if (!this.sellingPlanSelect || !this.purchaseOptionInputs) return;

        const selectedOption = Array.from(this.purchaseOptionInputs).find((input) => input.checked);
        const isSubscription = selectedOption && selectedOption.value === 'subscription';
        this.sellingPlanSelect.disabled = !isSubscription;
        this.purchaseOptions.classList.toggle('is-subscription-selected', isSubscription);
        this.purchaseOptions.classList.toggle('is-one-time-selected', !isSubscription);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            return this.addCaffynAddonItems(formData, response);
          })
          .then((response) => {
            if (!response) return;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.dataset.caffynShowUpsellStep = 'true';
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.handleErrorMessage(e.message || 'Unable to add this item to your cart.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      addCaffynAddonItems(originalFormData, originalResponse) {
        const addonInputs = Array.from(this.form.querySelectorAll('[data-caffyn-addon]:checked')).filter(
          (addonInput) => addonInput.dataset.variantId
        );
        if (!addonInputs.length) return Promise.resolve(originalResponse);

        const quantity = Number(originalFormData.get('quantity') || 1);
        const addonQuantity = Number.isFinite(quantity) && quantity > 0 ? String(quantity) : '1';

        return addonInputs.reduce((promise, addonInput) => {
          return promise.then(() => {
            const addonFormData = new FormData();
            addonFormData.append('id', addonInput.dataset.variantId);
            addonFormData.append('quantity', addonQuantity);
            addonFormData.append('properties[Add-on]', addonInput.dataset.addonName || 'Add-on');

            if (addonInput.dataset.addonPropertyName && addonInput.dataset.addonPropertyValue) {
              addonFormData.append(
                `properties[${addonInput.dataset.addonPropertyName}]`,
                addonInput.dataset.addonPropertyValue
              );
            }

            if (this.cart) {
              addonFormData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              addonFormData.append('sections_url', window.location.pathname);
            }

            const addonConfig = fetchConfig('javascript');
            addonConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete addonConfig.headers['Content-Type'];
            addonConfig.body = addonFormData;

            return fetch(`${routes.cart_add_url}`, addonConfig)
              .then((response) => response.json())
              .then((response) => {
                if (response.status) {
                  throw new Error(response.description || `Unable to add ${addonInput.dataset.addonName || 'add-on'}.`);
                }

                return response;
              });
          });
        }, Promise.resolve(originalResponse));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
